import type { RefObject } from "react";
import { useEffect } from "react";
import { ReaderAiHomeState } from "@/components/ui/reader-ai/home-state";
import { ReaderAiPromptComposer } from "@/components/ui/reader-ai/prompt-composer";
import { ToofiArtifactShelf } from "@/components/ui/reader-ai/toofi-artifact-shelf";
import { ToofiSidebar } from "@/components/ui/reader-ai/toofi-sidebar";
import { ReaderAiThreadList } from "@/components/ui/reader-ai/thread-list";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import type {
  ReaderAiThreadPanelSharedProps,
  ToofiShellSidebarView,
  ToofiWorkspaceView,
} from "@/components/ui/reader-ai/types";
import type { ToofiResumeTarget } from "@/types/student-toofi";

type ToofiThreadShellProps = ReaderAiThreadPanelSharedProps & {
  surfaceKind?: "reader" | "workspace";
  homeVariant?: "workspace-home" | "reader-home";
  isPhone?: boolean;
  isTablet?: boolean;
  focusMode?: boolean;
  fullScreen?: boolean;
  sidebarCollapsed?: boolean;
  showSidebar?: boolean;
  mainView?: ToofiWorkspaceView;
  onChromeEnter?: () => void;
  onChromeLeave?: () => void;
  bottomChromeRef?: RefObject<HTMLDivElement | null>;
  onThreadScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  homeSubtitle?: string;
  homePromptPlaceholder?: string;
  composerPlaceholder?: string;
  homeResumeTarget?: ToofiResumeTarget | null;
  onResumeTarget?: () => void;
  sidebarView?: ToofiShellSidebarView;
  onSidebarViewChange?: (view: ToofiShellSidebarView) => void;
  showHomeTemporaryAction?: boolean;
};

export function ToofiThreadShell({
  messages,
  threads,
  activeThreadId,
  activeReference,
  currentProductMode,
  onProductModeChange,
  onNewChat,
  onStartTemporaryChat,
  onStartChatWithPrompt,
  onSwitchThread,
  onRenameThread,
  onDeleteThread,
  onPinThread,
  onUnpinThread,
  onArchiveThread,
  onRestoreThread,
  onStartSimilarChat,
  onSetThreadMemoryMode,
  activeThreadMemoryMode,
  artifactItems,
  artifactItemsLoading,
  onOpenArtifact,
  onDeleteArtifact,
  onSetArtifactPinned,
  onGoToPage,
  onEditMessage,
  onDeleteMessage,
  onMcqSubmit,
  onPromptSubmit,
  onEssayStart,
  onEssayDraftChange,
  onEssayAttachmentAdd,
  onEssayAttachmentRemove,
  onEssayReadbackConfirm,
  onEssaySubmit,
  onGuidedLessonSubmit,
  onGuidedLessonContinue,
  onGuidedLessonSkip,
  onPlanSave,
  onPlanRefine,
  promptDraft,
  onPromptDraftChange,
  promptContextLabel,
  promptHelperText,
  selectionActivationMode,
  promptWarning = "none",
  onClearContext,
  onUseFileContext,
  onUseTextSelection,
  onUseVisualContext,
  focusMode = false,
  fullScreen = false,
  sidebarCollapsed = false,
  isPhone = false,
  isTablet = false,
  showSidebar,
  mainView,
  onChromeEnter,
  onChromeLeave,
  bottomChromeRef,
  onThreadScroll,
  surfaceKind = "reader",
  homeVariant,
  homeSubtitle,
  homePromptPlaceholder,
  composerPlaceholder,
  homeResumeTarget,
  onResumeTarget,
  sidebarView = "chats",
  onSidebarViewChange,
  showHomeTemporaryAction = false,
}: ToofiThreadShellProps) {
  const hasActiveThread = Boolean(activeThreadId);
  const canAttachReaderContext =
    surfaceKind === "reader" && (Boolean(onUseTextSelection) || Boolean(onUseVisualContext));
  const showComposerReferenceChrome =
    messages.length > 0 || Boolean(promptContextLabel) || (activeReference?.kind !== "none" && Boolean(activeReference));
  const shouldShowSidebar =
    showSidebar ?? (surfaceKind === "workspace" && !isPhone && !sidebarCollapsed);
  const resolvedMainView =
    mainView ?? (sidebarView === "artifacts" ? "artifacts" : hasActiveThread ? "thread" : "home");
  const sharedSidebarProps = {
    threads,
    activeThreadId,
    artifactCount: artifactItems?.length ?? 0,
    currentView: sidebarView,
    onViewChange: onSidebarViewChange ?? (() => undefined),
    onNewChat,
    onStartTemporaryChat,
    onSelectThread: (threadId: string) => {
      onSidebarViewChange?.("chats");
      onSwitchThread(threadId);
    },
    onRenameThread,
    onDeleteThread,
    onPinThread,
    onUnpinThread,
    onArchiveThread,
    onRestoreThread,
    onStartSimilarChat,
    onSetThreadMemoryMode,
  };

  useEffect(() => {
    if (import.meta.env.DEV && surfaceKind === "reader" && !canAttachReaderContext) {
      console.warn("[Toofi reader] Reader context actions are unavailable because no text or visual capture callback was provided.");
    }
  }, [canAttachReaderContext, surfaceKind]);

  const renderChatPanel = () => {
    if (resolvedMainView === "artifacts") {
      return (
        <div className="flex h-full min-h-0 flex-col">
          <ScrollArea className="min-h-0 flex-1">
            <div className={cn("mx-auto w-full px-4 py-5 sm:px-6 lg:px-8", fullScreen ? "max-w-6xl" : isTablet ? "max-w-4xl" : "max-w-5xl")}>
              <div className="mb-4">
                <h2 className="text-[1.1rem] font-semibold tracking-[-0.03em] text-slate-900">Saved study outputs</h2>
                <p className="mt-1 text-[12px] leading-5 text-slate-500">Plans, notes, quizzes, and reusable study artifacts.</p>
              </div>
              {onOpenArtifact && onDeleteArtifact && onSetArtifactPinned ? (
                <ToofiArtifactShelf
                  variant="workspace-panel"
                  artifacts={artifactItems ?? []}
                  isLoading={artifactItemsLoading}
                  onOpenArtifact={onOpenArtifact}
                  onDeleteArtifact={onDeleteArtifact}
                  onSetArtifactPinned={onSetArtifactPinned}
                />
              ) : null}
            </div>
          </ScrollArea>
        </div>
      );
    }

    if (resolvedMainView === "thread" && hasActiveThread) {
      return (
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
          <ReaderAiThreadList
            messages={messages}
            surfaceKind={surfaceKind}
            productMode={currentProductMode}
            focusMode={focusMode}
            isPhone={isPhone}
            isTablet={isTablet}
            onUseFileContext={onUseFileContext}
            onUseVisualContext={onUseVisualContext}
            onScroll={onThreadScroll}
            onGoToPage={onGoToPage}
            onEditMessage={onEditMessage}
            onDeleteMessage={onDeleteMessage}
            onMcqSubmit={onMcqSubmit}
            onFollowUpPrompt={onPromptSubmit}
            onEssayStart={onEssayStart}
            onEssayDraftChange={onEssayDraftChange}
            onEssayAttachmentAdd={onEssayAttachmentAdd}
            onEssayAttachmentRemove={onEssayAttachmentRemove}
            onEssayReadbackConfirm={onEssayReadbackConfirm}
            onEssaySubmit={onEssaySubmit}
            onGuidedLessonSubmit={onGuidedLessonSubmit}
            onGuidedLessonContinue={onGuidedLessonContinue}
            onGuidedLessonSkip={onGuidedLessonSkip}
            onPlanSave={onPlanSave}
            onPlanRefine={onPlanRefine}
          />
          <div
            ref={bottomChromeRef}
            className={cn(
              "pointer-events-none absolute z-30 select-none transition-all duration-300",
              isPhone
                ? "inset-x-0 bottom-0 px-2.5 pb-[max(0.65rem,env(safe-area-inset-bottom))]"
                : isTablet
                  ? "inset-x-3 bottom-3"
                  : "inset-x-[clamp(0.9rem,1.35vw,1.45rem)] bottom-[clamp(0.9rem,1.35vw,1.45rem)]",
              focusMode && "lg:inset-x-8",
            )}
          >
            <div
              className={cn("mx-auto flex w-full items-end justify-center", isPhone ? "max-w-none gap-2" : "max-w-full gap-2")}
              onPointerEnter={onChromeEnter}
              onPointerLeave={onChromeLeave}
              onFocusCapture={onChromeEnter}
              onBlurCapture={onChromeLeave}
            >
              <div className="pointer-events-auto min-w-0 flex-1">
                <ReaderAiPromptComposer
                  variant="thread"
                  embedded
                  surfaceKind={surfaceKind}
                  productMode={currentProductMode}
                  onProductModeChange={onProductModeChange}
                  placeholder={composerPlaceholder}
                  viewport={isPhone ? "phone" : isTablet ? "tablet" : "desktop"}
                  onSubmit={(prompt, selection) => onPromptSubmit(prompt, selection)}
                  draft={promptDraft}
                  onDraftChange={onPromptDraftChange}
                  contextLabel={showComposerReferenceChrome ? promptContextLabel : undefined}
                  activeReference={showComposerReferenceChrome ? activeReference : undefined}
                  selectionActivationMode={selectionActivationMode}
                  helperText={showComposerReferenceChrome ? promptHelperText : undefined}
                  onClearContext={onClearContext}
                  onSelectTextContext={canAttachReaderContext ? onUseTextSelection : undefined}
                  onUseVisualContext={canAttachReaderContext ? onUseVisualContext : undefined}
                  warningLevel={promptWarning}
                  onStartNewChat={onNewChat}
                  memoryMode={activeThreadMemoryMode}
                  onMemoryModeChange={activeThreadId ? (mode) => onSetThreadMemoryMode?.(activeThreadId, mode) : undefined}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <ReaderAiHomeState
        surfaceKind={surfaceKind}
        variant={homeVariant ?? (surfaceKind === "workspace" ? "workspace-home" : "reader-home")}
        subtitle={homeSubtitle}
        promptPlaceholder={homePromptPlaceholder}
        defaultProductMode={currentProductMode}
        resumeTarget={homeResumeTarget}
        onResumeTarget={onResumeTarget}
        onUseFileContext={surfaceKind === "reader" ? onUseFileContext : undefined}
        onStartTextContextCapture={canAttachReaderContext ? onUseTextSelection : undefined}
        onStartVisualContextCapture={canAttachReaderContext ? onUseVisualContext : undefined}
        onStartTemporaryChat={onStartTemporaryChat}
        onStartChatWithPrompt={onStartChatWithPrompt}
        showTemporaryAction={showHomeTemporaryAction}
        showResumeCard
      />
    );
  };

  if (!shouldShowSidebar) {
    return <div className="relative flex h-full min-w-0 flex-col overflow-hidden">{renderChatPanel()}</div>;
  }

  return (
    <div
      className={cn(
        "grid h-full min-w-0 overflow-hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(248,250,252,0.88))] transition-[grid-template-columns,border-radius,box-shadow,background] duration-300 ease-out",
        fullScreen
          ? "rounded-none border-0 shadow-none"
          : "rounded-[1.9rem] border border-[var(--athr-border-soft)] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]",
        isTablet ? "grid-cols-[15rem_minmax(0,1fr)]" : focusMode ? "grid-cols-[16rem_minmax(0,1fr)]" : "grid-cols-[16.5rem_minmax(0,1fr)]",
      )}
    >
      <aside
        className={cn(
          "min-h-0 overflow-hidden border-r border-[var(--athr-border-soft)] bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(248,250,252,0.78))] transition-all duration-300 ease-out",
          fullScreen && "bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(248,250,252,0.78))]",
        )}
      >
        <ToofiSidebar {...sharedSidebarProps} />
      </aside>
      <div
        className={cn(
          "min-h-0 overflow-hidden transition-[background] duration-300 ease-out",
          fullScreen
            ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.74),rgba(248,250,252,0.46))]"
            : "bg-[linear-gradient(180deg,rgba(255,255,255,0.5),rgba(248,250,252,0.24))]",
        )}
      >
        {renderChatPanel()}
      </div>
    </div>
  );
}
